import { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import * as Sentry from '@sentry/react'
import { MutationFunctionOptions, FetchResult } from '@apollo/client'
import {
  Exact,
  LogoutBusinessUserMutation,
  useLogoutBusinessUserMutation,
  useLogoutCreatorMutation,
  LogoutCreatorMutation,
  useLogoutUserMutation,
  LogoutUserMutation,
} from '../types/graphql'
import {
  initialBusinessUserProfile,
  initialBusinessUserToken,
  BusinessUserProfileContext,
  BusinessUserTokenContext,
  CreatorProfileContext,
  CreatorTokenContext,
  initialCreatorProfile,
  initialCreatorToken,
  UserProfileContext,
  UserTokenContext,
  initialUserProfile,
  initialUserToken,
} from '../context'

export const useBusinessUserLogout = (): ((
  options?:
    | MutationFunctionOptions<
        LogoutBusinessUserMutation,
        Exact<{
          [key: string]: never
        }>
      >
    | undefined,
) => Promise<FetchResult<LogoutBusinessUserMutation>>) => {
  const history = useHistory()

  const { updateBusinessUserToken } = useContext(BusinessUserTokenContext)
  const { updateBusinessId, updateBusinessName, updateBusinessEmail } = useContext(BusinessUserProfileContext)
  const [, , removeCookie] = useCookies(['token', 'businessUserToken'])

  const [logoutBusinessUserMutation] = useLogoutBusinessUserMutation({
    onCompleted: () => {
      history.push('/business/login')
      // cookie の削除を token の初期化より先に行う（理由は useCreatorLogout のコメント参照）
      removeCookie('businessUserToken', { path: '/' })
      updateBusinessUserToken(initialBusinessUserToken.businessUserToken)
      // プロフィールも初期化する。useSetMyProfile は name / email が空のときしか
      // 再取得しないため、初期化しないと同じタブで別アカウントがログインしても
      // 前のアカウントの情報が表示・編集フォームに残り続ける。
      updateBusinessId(initialBusinessUserProfile.businessId)
      updateBusinessName(initialBusinessUserProfile.businessName)
      updateBusinessEmail(initialBusinessUserProfile.businessEmail)
    },
    onError: (e) => {
      Sentry.captureException(e)
    },
  })

  return logoutBusinessUserMutation
}

export const useCreatorLogout = (): ((
  options?:
    | MutationFunctionOptions<
        LogoutCreatorMutation,
        Exact<{
          [key: string]: never
        }>
      >
    | undefined,
) => Promise<FetchResult<LogoutCreatorMutation>>) => {
  const history = useHistory()

  const { updateCreatorToken } = useContext(CreatorTokenContext)
  const { updateCreator } = useContext(CreatorProfileContext)
  const [, , removeCookie] = useCookies(['token', 'creatorToken'])

  const [logoutCreatorMutation] = useLogoutCreatorMutation({
    onCompleted: () => {
      history.push('/creator/login')
      // cookie の削除を token の初期化より先に行う。
      // 逆順にすると routes/creator.tsx の「token が空なら cookie から復元する」処理が
      // まだ残っている cookie の値で token を復活させてしまい、
      // ログアウト後もヘッダーがログイン状態（クリエイターのアイコン表示）のままになる。
      removeCookie('creatorToken', { path: '/' })
      updateCreatorToken(initialCreatorToken.creatorToken)
      // プロフィールの初期化は遷移後に行う。
      // マイページ表示中に初期化すると useSetCreatorProfile が再フェッチしてしまう。
      updateCreator(initialCreatorProfile.creator)
    },
    onError: (e) => {
      Sentry.captureException(e)
    },
  })

  return logoutCreatorMutation
}

export const useUserLogout = (): ((
  options?:
    | MutationFunctionOptions<
        LogoutUserMutation,
        Exact<{
          [key: string]: never
        }>
      >
    | undefined,
) => Promise<FetchResult<LogoutUserMutation>>) => {
  const history = useHistory()

  const { updateUserToken } = useContext(UserTokenContext)
  const { updateUser, updateFavoriteCreatorsNumber } = useContext(UserProfileContext)
  const [, , removeCookie] = useCookies(['token', 'userToken'])

  const [logoutUserMutation] = useLogoutUserMutation({
    onCompleted: () => {
      history.push('/login')
      // cookie の削除を token の初期化より先に行う（理由は useCreatorLogout のコメント参照）
      removeCookie('userToken', { path: '/' })
      updateUserToken(initialUserToken.userToken)
      // プロフィールも初期化する。マイページ編集画面は context の値を useState の
      // 初期値として一度だけ読むため、初期化しないと同じタブで別アカウントがログインした際に
      // 前のアカウントの情報がフォームに残る。
      updateUser(initialUserProfile.user)
      updateFavoriteCreatorsNumber(initialUserProfile.favoriteCreatorsNumber)
    },
    onError: (e) => {
      Sentry.captureException(e)
    },
  })

  return logoutUserMutation
}
